import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

export default function OrderForm() {
  const { t } = useTranslation();

  const productOptions = [
    {
      id: 'yoga-towel',
      name: t('pricing.yogaTowel'),
      price: '₺699',
      value: 'Yoga Towel - ₺699',
      image: 'https://public.readdy.ai/ai/img_res/08360e76-65a9-49f3-81cf-fd58070506ad.png'
    },
    {
      id: 'pilates-brown',
      name: t('pricing.pilatesSet') + ' - ' + t('products.brown'),
      price: '₺899',
      value: 'Pilates Reformer Set Brown - ₺899',
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/4b24b4ef28aebe02ef3709f34e7d5284.jpeg'
    },
    {
      id: 'pilates-crimson',
      name: t('pricing.pilatesSet') + ' - ' + t('products.crimson'),
      price: '₺899',
      value: 'Pilates Reformer Set Crimson - ₺899',
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/f9c94ee3587d5e76f51b1c0becee9c8b.png'
    },
    {
      id: 'complete-set',
      name: t('completeSet'),
      price: '₺1,499',
      value: 'Complete Wellness Set - ₺1,499',
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/dfd57959fbbeb3ed911f0129d055e576.jpeg'
    }
  ];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    product: '',
    quantity: '1',
    color: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleProductSelect = (productValue: string) => {
    setFormData({ ...formData, product: productValue });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.product || !formData.address) {
      alert(t('order.required'));
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const formResponse = await fetch('https://readdy.ai/api/form/d62svk30723ekv2it5a0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      const emailResponse = await fetch(`${SUPABASE_URL}/functions/v1/send-order-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (formResponse.ok && emailResponse.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          product: '',
          quantity: '1',
          color: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="order" className="py-24 bg-gradient-to-b from-red-50/30 to-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('order.title')}
          </h2>
          <p className="text-lg text-gray-600">
            {t('order.subtitle')}
          </p>
        </div>

        <form
          id="mysa-order-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl shadow-xl p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('order.fullName')} <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
                placeholder={t('order.fullName')}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('order.email')} <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('order.phone')}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
              placeholder="+90 (555) 000-0000"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              {t('order.selectProduct')} <span className="text-red-600">*</span>
            </label>
            <input type="hidden" name="product" value={formData.product} required />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {productOptions.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleProductSelect(product.value)}
                  className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                    formData.product === product.value
                      ? 'border-red-600 ring-2 ring-red-600/20 shadow-lg'
                      : 'border-gray-200 hover:border-red-300 hover:shadow-md'
                  }`}
                >
                  <div className="w-full h-24 bg-gray-50">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                  <div className="p-3 bg-white">
                    <p className="text-xs font-semibold text-gray-900 leading-tight mb-1">
                      {product.name}
                    </p>
                    <p className="text-sm font-bold text-red-600">{product.price}</p>
                  </div>
                  {formData.product === product.value && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {!formData.product && (
              <p className="text-xs text-gray-500 mt-2">{t('order.clickToSelect')}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="quantity" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('order.quantity')}
              </label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                min="1"
                max="10"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
              />
            </div>

            <div>
              <label htmlFor="color" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('order.colorPreference')}
              </label>
              <select
                id="color"
                name="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all cursor-pointer"
              >
                <option value="">{t('order.selectColor')}</option>
                <option value="Crimson Red">{t('order.crimsonRed')}</option>
                <option value="Earth Brown">{t('order.earthBrown')}</option>
                <option value="No Preference">{t('order.noPreference')}</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('order.streetAddress')} <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              required
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
              placeholder={t('order.streetAddress')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('order.city')}
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
                placeholder={t('order.city')}
              />
            </div>

            <div>
              <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('order.state')}
              </label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
                placeholder={t('order.state')}
              />
            </div>

            <div>
              <label htmlFor="zipCode" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('order.zipCode')}
              </label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all"
                placeholder={t('order.zipCode')}
              />
            </div>
          </div>

          <div className="mb-8">
            <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('order.additionalNotes')}
            </label>
            <textarea
              id="message"
              name="message"
              rows={4}
              maxLength={500}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all resize-none"
              placeholder={t('order.notesPlaceholder')}
            ></textarea>
            <div className="text-xs text-gray-500 mt-1 text-right">
              {formData.message.length}/500 {t('order.characters')}
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-2xl text-green-600"></i>
                <div>
                  <p className="font-semibold text-green-800">{t('order.success')}</p>
                  <p className="text-sm text-green-700">{t('order.successDesc')}</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-3">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
                <div>
                  <p className="font-semibold text-red-800">{t('order.error')}</p>
                  <p className="text-sm text-red-700">{t('order.errorDesc')}</p>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-red-600 text-white py-4 rounded-full text-base font-bold hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? t('order.submitting') : t('order.submit')}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            {t('order.terms')}
          </p>
        </form>
      </div>
    </section>
  );
}
